import { useEffect, useRef, useState } from "react";
import { ReactCropperElement } from "react-cropper";
import { useAppSelector } from "slice";
import {
    useGetUserPhotosQuery,
    usePutOrDeleteUserPhotoMutation,
    useSetUserPhotosMutation,
} from "slice/user";
import { PersonalImagesType } from "./types";

export const useImageUpload = () => {
    const { user_id } = useAppSelector((state) => state.userAuth),
        { data, refetch } = useGetUserPhotosQuery(user_id, {
            refetchOnMountOrArgChange: true,
        }),
        [uploadFile, { isSuccess: isUploadSuccess }] =
            useSetUserPhotosMutation(),
        [changeFile, { isSuccess: isChangeFileSuccess }] =
            usePutOrDeleteUserPhotoMutation();

    const [images, setImages] = useState<PersonalImagesType>({}),
        [isModalOpen, setIsModalOpen] = useState(false),
        [isProfile, setIsProfile] = useState(false);

    const cropperRef = useRef<ReactCropperElement>(null);

    const [imgSrc, setImgSrc] = useState("");

    const toggleModal = () => {
        setIsModalOpen((prev) => !prev);
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsProfile(e.target.id === "profilePic");
        if (e.target.id === "profilePic") {
            cropperRef.current?.cropper.setAspectRatio(1);
        } else {
            cropperRef.current?.cropper.setAspectRatio(600 / 200);
        }
        if (e.target.files && e.target.files.length > 0) {
            toggleModal();
            const reader = new FileReader();
            reader.addEventListener("load", () =>
                setImgSrc(reader.result?.toString() || "")
            );
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    const handleFileUpload = () => {
        const cropper = cropperRef.current?.cropper;
        const type = isProfile ? "profile" : "cover";
        if (cropper?.getCroppedCanvas()) {
            cropper?.getCroppedCanvas().toBlob((Blob) => {
                toggleModal();
                const formData = new FormData();
                if (Blob) {
                    formData.append("image", Blob);
                }
                formData.append("image_type", type);
                formData.append("user_id", `${user_id}`);

                const payload = {
                    method: "POST",
                    body: formData,
                    user_id: user_id,
                };

                if (images[type]?.id) {
                    changeFile({
                        ...payload,
                        put: true,
                        image_id: images[type]?.id,
                    });
                } else uploadFile(payload);
            });
        }
    };

    useEffect(() => {
        if (data && data.length > 0) {
            const profile =
                data.find((image: Image) => image.image_type === "profile") ||
                {};
            const cover =
                data.find((image: Image) => image.image_type === "cover") || {};
            if ((profile && profile?.url) || (cover && cover?.url)) {
                const url = profile?.url || cover?.url;
                if (url) {
                    const dummyImage = document.createElement("img");
                    dummyImage.addEventListener("error", () => {
                        refetch();
                    });
                    dummyImage.src = url;
                }
            }
            setImages({
                profile,
                cover,
            });
        }
    }, [data, refetch]);

    useEffect(() => {
        if (isUploadSuccess || isChangeFileSuccess) {
            refetch();
        }
    }, [isChangeFileSuccess, isUploadSuccess, refetch]);

    useEffect(() => {});

    return {
        images,
        handleFileUpload,
        handleImageChange,
        imgSrc,
        isModalOpen,
        toggleModal,
        cropperRef,
        isProfile,
    };
};
